// @ts-check
import classNames from 'classnames';
import React from 'react';
import GatsbyImage from './images/GatsbyImage';

import './Overview.scss';

/**
 * @typedef OverviewProps
 * @property {import('csstype').Property.TextAlign} [alignment='left']
 * @property {string} [border] The border style to apply to the inner container.
 * @property {import('react').CSSProperties} [containerStyle] The styles to spread onto the inner container.
 * @property {boolean} [isInExpandable=false] A value indicating whether this Overview is within an Expandable block.
 * @property {import('../CSTypes').BynderImageOptions} [leftImage=null] The optional image option to show.
 * @property {boolean} [omitBorderPadding=false] Reduces the border padding
 * @property {boolean} [includeLeftImageOnMobile=false] Displays left image on mobile
 */
/** @type {import('~/types/React').ReactFC<OverviewProps>} */
const Overview = ({
	alignment = 'left',
	isInExpandable = false,
	leftImage = null,
	omitBorderPadding = false,
	includeLeftImageOnMobile = false,
	id,
	...rest
}) => {
	return (
		<div {...rest} className={classNames('cs-overview', { container: !isInExpandable })}>
			<div
				id={id}
				className={classNames('cs-overview-container', `to-the-${alignment}`, rest.className)}
				style={{ border: rest.border }}
			>
				<div className={classNames('cs-overview-row', { row: !isInExpandable })}>
					{!!leftImage && (
						<GatsbyImage
							className={classNames(
								'cs-overview-image col-md-4',
								!includeLeftImageOnMobile && 'no-overview-image-on-mobile'
							)}
							option={leftImage}
							role="img"
						/>
					)}

					<div
						className={classNames(
							'cs-overview-content',
							`to-the-${alignment}`,
							!!omitBorderPadding && 'no-border-padding',
							!!leftImage ? 'col-md-8' : 'col-md-12'
						)}
					>
						{rest.children}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Overview;
